import { deadAfterFor, waitingPatchFromUids } from './asyncPlay.js';
import { mapDrawnBy } from '../engine/game';
import {
  ref as dbRef, onValue, onDisconnect, push, set, get, update, remove,
} from 'firebase/database';
import { db } from '../firebase';
import { generateRoomCode, normalizeRoomCode } from '../utils/roomCodes';
import { MIN_PLAYERS, MAX_PLAYERS } from '../engine/game';
import { BOT_NAMES } from '../engine/ai/heuristicBot';

export const GAMES = 'cartographersGames';
export const CODES = 'cartographersGameCodes';
export const USER_GAMES = 'cartographersUserGames';

// Rooms clean themselves up, same arrangement as the sibling games: presence
// plus an age limit, swept by each client for its own rooms when the lobby
// opens.
export const DEAD_AFTER_MS = {
  lobby: 60 * 60 * 1000,
  playing: 24 * 60 * 60 * 1000,
  over: 6 * 60 * 60 * 1000,
};

/**
 * WHY THERE IS NO SHARED GAME-STATE NODE AT ALL.
 *
 * Telestrations split its state into per-page nodes because simultaneous
 * submitters must not fight over one node. Cartographers goes one further:
 * the entire card script is a pure function of the seed (see
 * engine/script.js), and every map is a pure fold of the placements. So the
 * room stores exactly two game things - the seed, written once at start, and
 * `placements/<turn>/<map>`, each written once by the one seat drawing on
 * that map that turn. There is nothing left to transact over: the turn
 * "advances" on every client the moment the last placement of the turn
 * lands, because the fold says so.
 *
 * Placements are JSON STRINGS - a placement's cells are nested arrays, which
 * RTDB mangles (it has no faithful empty-array representation), the same
 * lesson every sibling repo learned once.
 */
export const submitPlacement = async ({ gameId, turnIndex, mapSeat, placement }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  await set(
    dbRef(db, `${GAMES}/${gameId}/placements/${turnIndex}/${mapSeat}`),
    JSON.stringify(placement),
  );
  update(dbRef(db, `${GAMES}/${gameId}/meta`), { lastActiveAt: Date.now() }).catch(() => {});
};

export const seatOfUid = (slots, uid) => {
  const slot = (slots || []).find((s) => s.uid === uid);
  return slot ? slot.seatIndex : -1;
};

/**
 * Who runs the bots (and the dropped): the connected human in the lowest
 * seat, derived from state every client already has - the house rule. With
 * no shared round node there is nothing else to drive; the write-once rule
 * on each placement is the backstop if two clients ever disagree.
 */
export const botDriver = (slots, presence) => {
  const connected = (slots || []).filter((s) => !s.isBot && presence?.[s.uid]);
  if (!connected.length) return null;
  return connected.reduce((best, s) => (s.seatIndex < best.seatIndex ? s : best)).uid;
};

export const lastActiveOf = (meta) => {
  if (Number.isFinite(meta?.lastActiveAt)) return meta.lastActiveAt;
  const parsed = Date.parse(meta?.createdAt || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

export const isDeadRoom = (meta, presence, now = Date.now()) => {
  if (!meta) return false;
  if (Object.keys(presence || {}).length > 0) return false;
  // An async room (asyncPlay.js) is meant to sit for days; it gets the long limits.
  const limit = deadAfterFor(meta, DEAD_AFTER_MS);
  return now - lastActiveOf(meta) > limit;
};

// ---- room lifecycle --------------------------------------------------------

export const botSlot = (index, seatIndex) => ({
  uid: `bot-${index}`,
  displayName: BOT_NAMES[index % BOT_NAMES.length],
  seatIndex,
  isBot: true,
});

export const setBotCount = async ({ user, gameId, meta, count }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host can change that.');
  if (meta.status !== 'lobby') throw new Error('The game has already started.');
  const humans = (meta.playerSlots || []).filter((s) => !s.isBot);
  const wanted = Math.max(0, Math.min(count, MAX_PLAYERS - humans.length));
  const slots = [
    ...humans.map((s, i) => ({ ...s, seatIndex: i })),
    ...Array.from({ length: wanted }, (_, i) => botSlot(i, humans.length + i)),
  ];
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots, lastActiveAt: Date.now() });
};

/**
 * Cartographers' async adapter (asyncPlay.js): the turn waits on every human
 * who has not placed on the map they are drawing on this turn (an ambush
 * passes maps round, so the map is looked up per seat). Bots are drawn for
 * by the lowest-seat human, so a turn where only bots remain waits on them.
 */
export const asyncWaitingOn = ({ view, players, placements }) => {
  if (!view || view.phase !== 'playing') return [];
  const n = players.length;
  const pending = players.filter((p, seat) => !placements?.[view.turnIndex]?.[mapDrawnBy(view.turn, seat, n)]);
  const humans = pending.filter((p) => !p.isBot).map((p) => p.id);
  if (humans.length) return humans;
  const anyHuman = players.filter((p) => !p.isBot);
  return pending.length && anyHuman.length ? [anyHuman[0].id] : [];
};

/** After a placement in an async room: who the turn waits on now. */
export const recordWaiting = async ({ gameId, view, players, placements, meta }) => {
  if (!db || !meta?.async || !view) return;
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), waitingPatchFromUids(asyncWaitingOn({ view, players, placements }), meta)).catch(() => {});
};

export const createRoom = async ({ user, async: asyncRoom = false }) => {
  if (!db) throw new Error('Online play needs Firebase configured (see .env.example).');
  if (!user) throw new Error('Sign in first.');

  const gameRef = push(dbRef(db, GAMES));
  const gameId = gameRef.key;

  let code;
  let attempts = 0;
  do {
    code = generateRoomCode(attempts);
    attempts += 1;
    // eslint-disable-next-line no-await-in-loop
  } while (attempts < 12 && (await get(dbRef(db, `${CODES}/${code}`))).exists());

  const meta = {
    hostUid: user.uid,
    status: 'lobby',
    async: asyncRoom,
    roomCode: code,
    createdAt: new Date().toISOString(),
    lastActiveAt: Date.now(),
    playerSlots: [{ uid: user.uid, displayName: user.displayName || 'Host', seatIndex: 0 }],
  };
  await set(dbRef(db, `${GAMES}/${gameId}/meta`), meta);
  await set(dbRef(db, `${CODES}/${code}`), gameId);
  await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: 'host', joinedAt: meta.createdAt });
  return { gameId, code };
};

export const joinRoom = async ({ user, code }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (!user) throw new Error('Sign in first.');

  const normalized = normalizeRoomCode(code);
  const codeSnap = await get(dbRef(db, `${CODES}/${normalized}`));
  if (!codeSnap.exists()) throw new Error(`No game found for code "${normalized}".`);
  const gameId = codeSnap.val();

  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) throw new Error('That game no longer exists.');
  const meta = metaSnap.val();
  const slots = meta.playerSlots || [];

  if (!slots.some((s) => s.uid === user.uid)) {
    // Once maps are being drawn a new seat can't be added - the fold has a
    // fixed player count. Rejoining a seat you already hold is always fine,
    // which is how coming back from a dropped connection works.
    if (meta.status !== 'lobby') throw new Error('That game has already started.');
    if (slots.length >= MAX_PLAYERS) throw new Error('That table is full.');
    const humans = slots.filter((s) => !s.isBot);
    const bots = slots.filter((s) => s.isBot);
    humans.push({ uid: user.uid, displayName: user.displayName || 'Guest', seatIndex: humans.length });
    slots.length = 0;
    slots.push(
      ...humans.map((s, i) => ({ ...s, seatIndex: i })),
      ...bots.map((s, i) => ({ ...s, seatIndex: humans.length + i })),
    );
    // Humans before bots; trim a bot if the join pushed past the cap.
    while (slots.length > MAX_PLAYERS && slots[slots.length - 1].isBot) slots.pop();
    await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots, lastActiveAt: Date.now() });
    await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: 'guest', joinedAt: new Date().toISOString() });
  }
  return { gameId };
};

export const leaveLobby = async ({ user, gameId }) => {
  if (!db || !user || !gameId) return;
  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) return;
  const meta = metaSnap.val();
  if (meta.status !== 'lobby') return;
  const slots = (meta.playerSlots || [])
    .filter((s) => s.uid !== user.uid)
    .map((s, i) => ({ ...s, seatIndex: i }));
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots });
  await remove(dbRef(db, `${GAMES}/${gameId}/presence/${user.uid}`));
};

export const startRoom = async ({ user, gameId, meta }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host can start.');
  const seats = (meta.playerSlots || []).length;
  if (seats < MIN_PLAYERS) throw new Error(`Cartographers here needs at least ${MIN_PLAYERS} players - add a robot.`);

  // The seed IS the game: script, scoring cards, everything (engine/script.js).
  const seed = (Date.now() ^ (Math.random() * 0xffffffff)) | 0;
  await set(dbRef(db, `${GAMES}/${gameId}/game`), { seed });
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), {
    status: 'playing',
    lastActiveAt: Date.now(),
    // An async room waits on everyone from the first turn (asyncPlay.js).
    ...(meta.async ? waitingPatchFromUids((meta.playerSlots || []).filter((s) => !s.isBot).map((s) => s.uid), meta) : {}),
  });
};

export const markOver = async ({ gameId }) => {
  if (!db) return;
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { status: 'over', lastActiveAt: Date.now() })
    .catch(() => {});
};

export const bindRoom = ({ gameId, user, onMeta, onGame, onPlacements, onPresence }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  const at = (path) => dbRef(db, `${GAMES}/${gameId}/${path}`);
  const offs = [
    onValue(at('meta'), (s) => onMeta(s.val())),
    onValue(at('game'), (s) => onGame(s.val())),
    onValue(at('placements'), (s) => onPlacements(s.val() || {})),
    onValue(at('presence'), (s) => onPresence(s.val() || {})),
  ];

  let myPresenceRef = null;
  if (user) {
    myPresenceRef = at(`presence/${user.uid}`);
    onDisconnect(myPresenceRef).remove();
    set(myPresenceRef, true);
    update(at('meta'), { lastActiveAt: Date.now() }).catch(() => {});
  }

  return () => {
    offs.forEach((off) => off());
    if (myPresenceRef) remove(myPresenceRef);
  };
};

/**
 * Deletes the caller's own dead rooms - self-service because that is all the
 * rules permit (no client can list every room; only a host deletes theirs).
 */
export const sweepMyRooms = async ({ user, now = Date.now(), database = db }) => {
  if (!database || !user) return { deleted: 0, pruned: 0 };
  let deleted = 0;
  let pruned = 0;
  const mine = await get(dbRef(database, `${USER_GAMES}/${user.uid}`));
  if (!mine.exists()) return { deleted, pruned };

  for (const [gameId, entry] of Object.entries(mine.val() || {})) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const metaSnap = await get(dbRef(database, `${GAMES}/${gameId}/meta`));
      if (!metaSnap.exists()) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`));
        pruned += 1;
        continue;
      }
      if (entry?.role !== 'host' && metaSnap.val().hostUid !== user.uid) continue;
      // eslint-disable-next-line no-await-in-loop
      const presenceSnap = await get(dbRef(database, `${GAMES}/${gameId}/presence`));
      const meta = metaSnap.val();
      if (!isDeadRoom(meta, presenceSnap.val() || {}, now)) continue;
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${GAMES}/${gameId}`));
      if (meta.roomCode) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${CODES}/${meta.roomCode}`)).catch(() => {});
      }
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`)).catch(() => {});
      deleted += 1;
    } catch {
      // Never let one room stop the sweep.
    }
  }
  return { deleted, pruned };
};
